import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useStageMapping } from "@/hooks/useStageMapping";
import { isCategoryAtOrDeeper, type VentureCategory } from "@/lib/stage-categories";

export type FunnelEvent = Database["public"]["Tables"]["funnel_events"]["Row"];
export type CampaignMetricRow = Database["public"]["Tables"]["campaign_metrics"]["Row"];

export type MapStageFn = (stageId: string) => VentureCategory;

export interface KPIs {
  totalLeads: number;
  demosBooked: number;
  demosClosed: number;
  totalRevenueCents: number;
  showRate: number;
  closeRate: number;
  totalSpendCents: number;
  totalImpressions: number;
  totalClicks: number;
  totalConversions: number;
  costPerLeadCents: number;
  costPerDemoCents: number;
  overallCTR: number;
  overallROAS: number | null;
}

export interface CampaignTotalRow {
  campaign_id: string;
  campaign_name: string;
  platform: string;
  impressions: number;
  clicks: number;
  link_clicks: number;
  reach: number;
  frequency: number;
  spend_cents: number;
  conversions: number;
  cpc_cents: number;
  cpm_cents: number;
  ctr: number;
  cost_per_result_cents: number;
  date_start: string;
  date_end: string;
}

export interface CampaignData {
  campaignId: string;
  campaignName: string;
  platform: string;
  leads: number;
  demosBooked: number;
  demosClosed: number;
  totalRevenueCents: number;
  roas: number | null;
  conversionRate: number;
  adSets: AdSetData[];
  recentLeads: FunnelEvent[];
  // Ad metrics — from campaign_totals when available, else summed from daily
  impressions: number;
  clicks: number;
  linkClicks: number;
  reach: number;
  frequency: number;
  conversions: number;
  spendCents: number;
  ctr: number;
  cpcCents: number;
  cpmCents: number;
  cplCents: number;
  cpdCents: number;
  costPerResultCents: number;
  dateRange: { earliest: string; latest: string } | null;
  dailyData: { date: string; spendCents: number; impressions: number; clicks: number; conversions: number }[];
}

export interface AdSetData {
  adSetId: string;
  adSetName: string;
  leads: number;
  demosBooked: number;
  demosClosed: number;
  spendCents: number;
}

export interface SetterData {
  setterId: string;
  setterName: string;
  demosBooked: number;
  showed: number;
  demosClosed: number;
  showRate: number;
  closeRate: number;
  recentBookings: FunnelEvent[];
}

export interface CloserData {
  closerId: string;
  closerName: string;
  demosTaken: number;
  demosClosed: number;
  totalRevenueCents: number;
  avgDealCents: number;
  closeRate: number;
  closedDeals: FunnelEvent[];
}

export function computeKPIs(events: FunnelEvent[], metrics: CampaignMetricRow[], mapStage: MapStageFn): KPIs {
  const categorized = events.map(e => ({
    ...e,
    category: mapStage(e.stage),
  }));

  const leads = categorized.filter(e => e.category !== 'unmapped');
  const booked = categorized.filter(e =>
    isCategoryAtOrDeeper(e.category, 'demo_booked')
  );
  const completed = categorized.filter(e =>
    isCategoryAtOrDeeper(e.category, 'demo_completed')
  );
  const closed = categorized.filter(e => e.category === 'closed_won');
  const totalRevenueCents = closed.reduce((sum, e) => sum + (e.deal_value_cents ?? 0), 0);

  // Ad metrics from campaign_metrics
  const totalSpendCents = metrics.reduce((s, m) => s + (m.spend_cents ?? 0), 0);
  const totalImpressions = metrics.reduce((s, m) => s + (m.impressions ?? 0), 0);
  const totalClicks = metrics.reduce((s, m) => s + (m.clicks ?? 0), 0);
  const totalConversions = metrics.reduce((s, m) => s + (m.conversions ?? 0), 0);
  const effectiveLeads = totalConversions > 0 ? totalConversions : leads.length;

  return {
    totalLeads: leads.length,
    demosBooked: booked.length,
    demosClosed: closed.length,
    totalRevenueCents,
    showRate: booked.length > 0 ? Math.round((completed.length / booked.length) * 100) : 0,
    closeRate: completed.length > 0 ? Math.round((closed.length / completed.length) * 100) : 0,
    totalSpendCents,
    totalImpressions,
    totalClicks,
    totalConversions,
    costPerLeadCents: effectiveLeads > 0 ? Math.round(totalSpendCents / effectiveLeads) : 0,
    costPerDemoCents: booked.length > 0 ? Math.round(totalSpendCents / booked.length) : 0,
    overallCTR: totalImpressions > 0 ? Math.round((totalClicks / totalImpressions) * 10000) / 100 : 0,
    overallROAS: totalSpendCents > 0 ? Math.round((totalRevenueCents / totalSpendCents) * 10) / 10 : null,
  };
}

export function computeCampaigns(events: FunnelEvent[], metrics: CampaignMetricRow[], totals: CampaignTotalRow[], mapStage: MapStageFn): CampaignData[] {
  const byCampaign = new Map<string, FunnelEvent[]>();
  for (const e of events) {
    const key = e.campaign_id ?? "__unknown__";
    if (!byCampaign.has(key)) byCampaign.set(key, []);
    byCampaign.get(key)!.push(e);
  }
  // Build totals lookup (from campaign_totals — correct reach, frequency, results)
  const totalsByCampaign = new Map<string, CampaignTotalRow>();
  for (const t of totals) {
    totalsByCampaign.set(t.campaign_id, t);
  }

  const spendByCampaign = new Map<string, number>();
  const metricsByCampaignId = new Map<string, CampaignMetricRow[]>();
  for (const m of metrics) {
    spendByCampaign.set(m.campaign_id, (spendByCampaign.get(m.campaign_id) ?? 0) + (m.spend_cents ?? 0));
    if (!metricsByCampaignId.has(m.campaign_id)) metricsByCampaignId.set(m.campaign_id, []);
    metricsByCampaignId.get(m.campaign_id)!.push(m);
  }
  const campaignResults = Array.from(byCampaign.entries()).map(([campaignId, evts]) => {
    const first = evts[0];
    const categorized = evts.map(e => ({ ...e, category: mapStage(e.stage) }));
    const booked = categorized.filter(e => isCategoryAtOrDeeper(e.category, 'demo_booked')).length;
    const closed = categorized.filter(e => e.category === 'closed_won');
    const totalRevenueCents = closed.reduce((s, e) => s + (e.deal_value_cents ?? 0), 0);
    const spendCents = spendByCampaign.get(campaignId) ?? 0;
    const roas = spendCents > 0 ? Math.round((totalRevenueCents / spendCents) * 10) / 10 : null;
    const byAdSet = new Map<string, FunnelEvent[]>();
    for (const e of evts) {
      if (!e.ad_set_id) continue;
      if (!byAdSet.has(e.ad_set_id)) byAdSet.set(e.ad_set_id, []);
      byAdSet.get(e.ad_set_id)!.push(e);
    }
    const adSets: AdSetData[] = Array.from(byAdSet.entries()).map(([adSetId, aevts]) => {
      const adCategorized = aevts.map(e => ({ ...e, category: mapStage(e.stage) }));
      return {
        adSetId,
        adSetName: aevts[0].ad_set_name ?? "Default",
        leads: aevts.length,
        demosBooked: adCategorized.filter(e => isCategoryAtOrDeeper(e.category, 'demo_booked')).length,
        demosClosed: adCategorized.filter(e => e.category === 'closed_won').length,
        spendCents: metrics.filter(m => m.ad_set_id === adSetId).reduce((s, m) => s + (m.spend_cents ?? 0), 0),
      };
    });
    // Map stage UUIDs to category names for recent leads display
    const recentLeads = evts.slice(0, 10).map(e => ({
      ...e,
      stage: mapStage(e.stage), // Resolve UUID to category name
    }));
    const campaignMetrics = metricsByCampaignId.get(campaignId) ?? [];
    const total = totalsByCampaign.get(campaignId);
    // Use campaign_totals for accurate aggregated metrics, fall back to summing daily rows
    const impressions = total?.impressions ?? campaignMetrics.reduce((s, m) => s + (m.impressions ?? 0), 0);
    const clicks = total?.clicks ?? campaignMetrics.reduce((s, m) => s + (m.clicks ?? 0), 0);
    const linkClicks = total?.link_clicks ?? 0;
    const reach = total?.reach ?? 0;
    const frequency = total?.frequency ?? 0;
    const conversions = total?.conversions ?? campaignMetrics.reduce((s, m) => s + (m.conversions ?? 0), 0);
    const cpcCents = total?.cpc_cents ?? (clicks > 0 ? spendCents / clicks : 0);
    const cpmCents = total?.cpm_cents ?? (impressions > 0 ? (spendCents / impressions) * 1000 : 0);
    const ctrValue = total?.ctr ?? (impressions > 0 ? (clicks / impressions) * 100 : 0);
    const costPerResultCents = total?.cost_per_result_cents ?? (conversions > 0 ? spendCents / conversions : 0);
    const dates = total ? [total.date_start, total.date_end] : campaignMetrics.map(m => m.date).filter(Boolean).sort();
    return {
      campaignId,
      campaignName: first.campaign_name ?? (campaignId === "__unknown__" ? "Leads" : campaignId),
      platform: first.platform ?? "GHL",
      leads: evts.length,
      demosBooked: booked,
      demosClosed: closed.length,
      totalRevenueCents,
      roas,
      conversionRate: evts.length > 0 ? Math.round((booked / evts.length) * 100) : 0,
      adSets,
      recentLeads,
      impressions,
      clicks,
      linkClicks,
      reach,
      frequency,
      conversions,
      spendCents,
      ctr: ctrValue,
      cpcCents,
      cpmCents,
      cplCents: conversions > 0 ? spendCents / conversions : 0,
      cpdCents: booked > 0 ? spendCents / booked : 0,
      costPerResultCents,
      dateRange: dates.length > 0 ? { earliest: dates[0], latest: dates[dates.length - 1] } : null,
      dailyData: campaignMetrics.map(m => ({ date: m.date, spendCents: m.spend_cents ?? 0, impressions: m.impressions ?? 0, clicks: m.clicks ?? 0, conversions: m.conversions ?? 0 })).sort((a, b) => a.date.localeCompare(b.date)),
    };
  });

  // Add campaign_metrics entries that don't have funnel_events (Meta/Google campaigns with spend but no leads yet)
  const eventCampaignIds = new Set(byCampaign.keys());
  const metricsByCampaign = new Map<string, CampaignMetricRow[]>();
  for (const m of metrics) {
    if (eventCampaignIds.has(m.campaign_id)) continue; // Already in results from funnel_events
    if (!metricsByCampaign.has(m.campaign_id)) metricsByCampaign.set(m.campaign_id, []);
    metricsByCampaign.get(m.campaign_id)!.push(m);
  }

  for (const [campaignId, rows] of metricsByCampaign) {
    const first = rows[0];
    const total = totalsByCampaign.get(campaignId);
    const totalSpend = total?.spend_cents ?? rows.reduce((s, m) => s + (m.spend_cents ?? 0), 0);
    const impressions = total?.impressions ?? rows.reduce((s, m) => s + (m.impressions ?? 0), 0);
    const clicks = total?.clicks ?? rows.reduce((s, m) => s + (m.clicks ?? 0), 0);
    const conversions = total?.conversions ?? rows.reduce((s, m) => s + (m.conversions ?? 0), 0);
    const dates = total ? [total.date_start, total.date_end] : rows.map(m => m.date).filter(Boolean).sort();
    campaignResults.push({
      campaignId,
      campaignName: first.campaign_name ?? campaignId,
      platform: (first as any).platform ?? "ads",
      leads: 0,
      demosBooked: 0,
      demosClosed: 0,
      totalRevenueCents: 0,
      roas: null,
      conversionRate: 0,
      adSets: [],
      recentLeads: [],
      impressions,
      clicks,
      linkClicks: total?.link_clicks ?? 0,
      reach: total?.reach ?? 0,
      frequency: total?.frequency ?? 0,
      conversions,
      spendCents: totalSpend,
      ctr: total?.ctr ?? (impressions > 0 ? (clicks / impressions) * 100 : 0),
      cpcCents: total?.cpc_cents ?? (clicks > 0 ? totalSpend / clicks : 0),
      cpmCents: total?.cpm_cents ?? (impressions > 0 ? (totalSpend / impressions) * 1000 : 0),
      cplCents: conversions > 0 ? totalSpend / conversions : 0,
      cpdCents: 0,
      costPerResultCents: total?.cost_per_result_cents ?? (conversions > 0 ? totalSpend / conversions : 0),
      dateRange: dates.length > 0 ? { earliest: dates[0], latest: dates[dates.length - 1] } : null,
      dailyData: rows.map(m => ({ date: m.date, spendCents: m.spend_cents ?? 0, impressions: m.impressions ?? 0, clicks: m.clicks ?? 0, conversions: m.conversions ?? 0 })).sort((a, b) => a.date.localeCompare(b.date)),
    });
  }

  return campaignResults;
}

export function computeSetters(events: FunnelEvent[], mapStage: MapStageFn): SetterData[] {
  const bySetter = new Map<string, FunnelEvent[]>();
  for (const e of events) {
    if (!e.setter_id) continue;
    if (!bySetter.has(e.setter_id)) bySetter.set(e.setter_id, []);
    bySetter.get(e.setter_id)!.push(e);
  }
  return Array.from(bySetter.entries()).map(([setterId, evts]) => {
    const first = evts[0];
    const categorized = evts.map(e => ({ ...e, category: mapStage(e.stage) }));
    const booked = categorized.filter(e => isCategoryAtOrDeeper(e.category, 'demo_booked'));
    const showed = categorized.filter(e => isCategoryAtOrDeeper(e.category, 'demo_completed'));
    const closed = categorized.filter(e => e.category === 'closed_won');
    return {
      setterId,
      setterName: first.setter_name ?? "Unassigned",
      demosBooked: booked.length,
      showed: showed.length,
      demosClosed: closed.length,
      showRate: booked.length > 0 ? Math.round((showed.length / booked.length) * 100) : 0,
      closeRate: showed.length > 0 ? Math.round((closed.length / showed.length) * 100) : 0,
      recentBookings: booked.slice(0, 10),
    };
  });
}

export function computeClosers(events: FunnelEvent[], mapStage: MapStageFn): CloserData[] {
  const byCloser = new Map<string, FunnelEvent[]>();
  for (const e of events) {
    if (!e.closer_id) continue;
    if (!byCloser.has(e.closer_id)) byCloser.set(e.closer_id, []);
    byCloser.get(e.closer_id)!.push(e);
  }
  return Array.from(byCloser.entries()).map(([closerId, evts]) => {
    const first = evts[0];
    const categorized = evts.map(e => ({ ...e, category: mapStage(e.stage) }));
    const showed = categorized.filter(e => isCategoryAtOrDeeper(e.category, 'demo_completed'));
    const closed = categorized.filter(e => e.category === 'closed_won');
    const totalRevenueCents = closed.reduce((s, e) => s + (e.deal_value_cents ?? 0), 0);
    return {
      closerId,
      closerName: first.closer_name ?? "Unassigned",
      demosTaken: showed.length,
      demosClosed: closed.length,
      totalRevenueCents,
      avgDealCents: closed.length > 0 ? Math.round(totalRevenueCents / closed.length) : 0,
      closeRate: showed.length > 0 ? Math.round((closed.length / showed.length) * 100) : closed.length > 0 ? 100 : 0,
      closedDeals: closed.slice(0, 10),
    };
  });
}

export type DateRange = { from: Date; to: Date };

export interface AttributionData {
  kpis: KPIs;
  campaigns: CampaignData[];
  setters: SetterData[];
  closers: CloserData[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAttributionData(dateRange: DateRange, clientId?: string): AttributionData {
  const [events, setEvents] = useState<FunnelEvent[]>([]);
  const [metrics, setMetrics] = useState<CampaignMetricRow[]>([]);
  const [totals, setTotals] = useState<CampaignTotalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const { mapStage, isLoading: mappingsLoading } = useStageMapping(clientId);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const fromStr = dateRange.from.toISOString();
        const toStr = dateRange.to.toISOString();
        let eventsQuery = supabase.from("funnel_events").select("*")
          .gte("lead_created_at", fromStr).lte("lead_created_at", toStr);
        let metricsQuery = supabase.from("campaign_metrics").select("*")
          .gte("date", dateRange.from.toISOString().split("T")[0])
          .lte("date", dateRange.to.toISOString().split("T")[0]);
        let totalsQuery = supabase.from("campaign_totals").select("*");
        if (clientId) {
          eventsQuery = eventsQuery.eq("client_id", clientId);
          metricsQuery = metricsQuery.eq("client_id", clientId);
          totalsQuery = totalsQuery.eq("client_id", clientId);
        }
        const [eventsResult, metricsResult, totalsResult] = await Promise.all([eventsQuery, metricsQuery, totalsQuery]);
        if (eventsResult.error) throw eventsResult.error;
        if (metricsResult.error) throw metricsResult.error;
        if (!cancelled) {
          setEvents(eventsResult.data ?? []);
          setMetrics(metricsResult.data ?? []);
          setTotals((totalsResult.data ?? []) as unknown as CampaignTotalRow[]);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [dateRange.from.toISOString(), dateRange.to.toISOString(), clientId, tick]);

  const kpis = useMemo(() => computeKPIs(events, metrics, mapStage), [events, metrics, mapStage]);
  const campaigns = useMemo(() => computeCampaigns(events, metrics, totals, mapStage), [events, metrics, totals, mapStage]);
  const setters = useMemo(() => computeSetters(events, mapStage), [events, mapStage]);
  const closers = useMemo(() => computeClosers(events, mapStage), [events, mapStage]);

  return {
    kpis,
    campaigns,
    setters,
    closers,
    loading: loading || mappingsLoading,
    error,
    refetch: () => setTick(t => t + 1),
  };
}
